import { BlockItemContainer } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDeleteActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useDeleteActionPlanAction";
import { useUpsertActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useUpsertActionPlanAction";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useSnackbar } from "src/utils/useSnackbar";
import { ActionType } from "./ActionsBlock/ActionRow";
import { ActionsBlock } from "./ActionsBlock/ActionsBlock";
import { useRecalculateActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useRecalculateActionPlan";
import { useQueryClient } from "react-query";
import { getAllActionPlanActionsKey } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { ACTION_STATUS } from "src/components/Actions/Common/actionStatus";

export type ActionBlockProps = {
    sectionTitle: string;
    score: number | null;
    blocks: ActionType[];
};

export const ActionBlock = (props: ActionBlockProps) => {
    const { sectionTitle, score, blocks } = props;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();

    const { urls, selectedScore, actionPlan, actionPlanMetadata, savingStatus } = useActionsContext();
    const createActionPlanAction = useUpsertActionPlanAction({ actionPlanID: actionPlan?.id });
    const recalculate = useRecalculateActionPlan(actionPlanMetadata.actionPlanID);
    const deleteActionPlanAction = useDeleteActionPlanAction();

    return (
        <BlockItemContainer
            title={sectionTitle}
            value={score}
            unit={selectedScore?.unit}
            fields={[
                <ActionsBlock
                    isLoading={false}
                    actionBlocks={blocks}
                    addAction={{
                        helperText: t("action.addAction"),
                        action: (option) => {
                            // Backend doesn't send it...
                            const iriF = (s: number | string) => `/api/ssq_posts/${s}`;
                            if (
                                actionPlanMetadata.actionPlanIRI === undefined ||
                                actionPlanMetadata.firstPostIdWithActionPlan === undefined ||
                                actionPlanMetadata.targetPostIdWithActionPlan === undefined
                            )
                                return;

                            savingStatus.setSaving(true);
                            createActionPlanAction.mutate(
                                {
                                    id: undefined,
                                    actionId: option.actionId,
                                    actionPlan: actionPlanMetadata.actionPlanIRI,
                                    startSimulationSsqPost: iriF(actionPlanMetadata.firstPostIdWithActionPlan),
                                    endSimulationSsqPost: iriF(actionPlanMetadata.targetPostIdWithActionPlan),
                                    type: ACTION_STATUS[0].backendLabel,
                                },
                                {
                                    onSuccess: (resp) => {
                                        snackbar.forceClose();
                                        snackbar.setSnackbarProps({
                                            type: "success",
                                            message: "actionPlan.takeAction.created",
                                        });
                                        queryClient.refetchQueries([getAllActionPlanActionsKey]);

                                        const idToRedirect = resp?.data.id;
                                        if (idToRedirect !== undefined) {
                                            const url = urls.editPlanning(idToRedirect);
                                            navigate(url);
                                        }
                                    },
                                    onError: () => {
                                        snackbar.forceClose();
                                        snackbar.setSnackbarProps({
                                            type: "error",
                                            message: t("actionPlan.takeAction.error"),
                                        });
                                    },
                                    onSettled: () => {
                                        savingStatus.setSaving(false);
                                    },
                                },
                            );
                        },
                    }}
                    editAction={{
                        helperText: t("action.modifyAction"),
                        action: (option) => {
                            if (option.id === null) return;

                            const reallyAString = option.id;
                            const url = urls.editPlanning(reallyAString);
                            navigate(url);
                        },
                    }}
                    deleteAction={{
                        helperText: t("action.deleteAction"),
                        action: (option) => {
                            if (option.id === null) return;
                            const payload = {
                                actionPlanActionId: option.id,
                                actionPlanId: actionPlanMetadata.actionPlanID,
                            };
                            deleteActionPlanAction.mutate(payload, {
                                onSuccess: () => {
                                    recalculate.mutate(null);
                                },
                            });
                        },
                    }}
                />,
            ]}
        />
    );
};
